import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./AuthProvider";
import {
  agreeTerms,
  completeOnboarding,
  changeTier,
  cancelSubscription,
  friendlyMessage,
  getMe,
  getSubscription,
  getSubscriptionHistory,
  updateCompanyProfile,
  updateIndividualProfile,
  updateMe,
} from "./accountApi";
import type {
  Account,
  ChangeTierRequest,
  CompleteOnboardingRequest,
  CompanyProfile,
  IndividualProfile,
  Subscription,
  SubscriptionEvent,
  UpdateAccountRequest,
} from "../models/account";

/** Loads and mutates the current user's account + subscription context. */
export function useAccount() {
  const { status, token } = useAuth();
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const getToken = useMemo(() => () => tokenRef.current(), []);

  const [account, setAccount] = useState<Account | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [history, setHistory] = useState<SubscriptionEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const me = await getMe(getToken);
      setAccount(me);
      // The subscription only exists once the account is created (onboarding complete). Skip it for
      // a not-yet-onboarded caller (the /api/account/me prefill) — the subscription endpoint would 428.
      setSubscription(me.onboarded ? await getSubscription(getToken) : null);
    } catch (e) {
      setError(friendlyMessage(e));
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  const loadHistory = useCallback(async () => {
    try {
      setHistory(await getSubscriptionHistory(getToken));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }, [getToken]);

  useEffect(() => {
    if (status === "authenticated") void reload();
  }, [status, reload]);

  async function saveAccount(req: UpdateAccountRequest) {
    setAccount(await updateMe(getToken, req));
  }
  async function saveIndividualProfile(profile: IndividualProfile) {
    setAccount(await updateIndividualProfile(getToken, profile));
  }
  async function saveCompanyProfile(profile: CompanyProfile) {
    setAccount(await updateCompanyProfile(getToken, profile));
  }
  async function acceptTerms() {
    setAccount(await agreeTerms(getToken));
  }
  async function onboard(req: CompleteOnboardingRequest) {
    const acc = await completeOnboarding(getToken, req);
    setAccount(acc);
    if (acc.onboarded) setSubscription(await getSubscription(getToken));
    return acc;
  }
  async function changeSubscriptionTier(req: ChangeTierRequest) {
    setSubscription(await changeTier(getToken, req));
    await loadHistory();
  }
  async function cancelMySubscription() {
    setSubscription(await cancelSubscription(getToken));
    await loadHistory();
  }

  return {
    account,
    subscription,
    history,
    loading,
    error,
    reload,
    loadHistory,
    saveAccount,
    saveIndividualProfile,
    saveCompanyProfile,
    acceptTerms,
    onboard,
    changeSubscriptionTier,
    cancelMySubscription,
  };
}
